import {
  ADD_TO_BASKET_ESI_CHILD_ITEMS_UPDATED_POST_A2B,
  ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
} from "@/constants/constants";
import { Drawer } from "@JohnLewisPartnership/jl-design-system/dist/components/drawer/Drawer";
import styles from "./ProtectPlusComplianceDrawer.module.scss";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { TickOutlined as Tick } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";

import {
  BasketItem,
  getBasket,
  ServiceItem,
} from "@/utils/analytics/getBasket.tsx";
import { VariantServices } from "@/types/types.ts";
import {
  Services,
  TransformedExtendedWarrantyMulti,
} from "@/types/serviceTypes.ts";
import { SharedServicesActions } from "@/utils/services/useSharedServices";
import {
  BasketMode,
  OPEN_POST_ADD_TO_BASKET_DRAWER,
  PRE_ADD_TO_BASKET_DRAWER,
} from "./components/AvailableServices";

import { ProtectPlusScheme } from "@/components/PostAddToBasketDrawer/scheme/schemeDecisioner.ts";
import { ProtectPlusCompliance } from "@/components/PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusCompliance.tsx";
import {
  addServiceToBasket,
  changeItemQuantity,
} from "@/components/PostAddToBasketDrawer/data/basketServicesApi.ts";
import classNames from "classnames";

type Props = {
  title: string;
  variantServices: VariantServices;
  basketBaseUrl: string;
  productId: string;
  skuId: string;
  guarantees?: { title: string }[];
  basketMode: BasketMode;
  protectPlusScheme: ProtectPlusScheme;
  hideFooter: boolean;
  mandatoryServicesOverride: string[];
  /** Available services — passed down from the container. */
  availableServices: Services;
  /** Store mutation actions — passed down from the container. */
  actions: SharedServicesActions;
};

type CachedBasketProduct = {
  quantity: number;
  itemId: string;
  services: Array<ServiceItem>;
};

export const postAddToBasketDrawerEvent = "POST_ADD_TO_BASKET_DRAWER_OPENED";

const findCachedBasketProduct: (
  skuId: string,
) => CachedBasketProduct | undefined = (skuId: string) => {
  const product: BasketItem | undefined = getBasket().product.find(
    (i) => i.sku === skuId,
  );

  if (!product) {
    return;
  }

  return {
    quantity: parseInt(product.qty, 10),
    itemId: product.itemId,
    services: product.services || [],
  };
};

export const ProtectPlusComplianceDrawerUI = ({
  title,
  variantServices,
  skuId,
  basketMode,
  guarantees = [],
  protectPlusScheme,
  basketBaseUrl,
  hideFooter,
  availableServices = [],
  actions,
}: Props) => {
  const drawerHeaderDefault = "Protect Plus";
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const commerceItems = useRef<null | Array<any>>(null);
  const isBasketFull = useRef<boolean>(false);
  const [complianceDrawerHeader, setComplianceDrawerHeader] =
    useState<string>(drawerHeaderDefault);
  const [basketQuantity, setBasketQuantity] = useState(0);
  const [apiError, setApiError] = useState(false);
  const [localQuantity, setLocalQuantity] = useState(0);
  const showTitleWithoutTick = hideFooter || basketQuantity === 0;
  const extendedWarrantySkuIds = useMemo(
    () =>
      variantServices
        ?.filter((service) => service.__typename === "ExtendedWarranty")
        .map((service) => service.aliases.skuId) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const pplusServiceBase = availableServices.find(
    (s) => s.serviceType === "ExtendedWarranty",
  );

  const warrantyService: TransformedExtendedWarrantyMulti | undefined =
    pplusServiceBase
      ? { ...pplusServiceBase, quantity: localQuantity }
      : undefined;

  useEffect(() => {
    const cachedBasketProduct = findCachedBasketProduct(skuId);
    const pplusInBasket = cachedBasketProduct?.services.find((s) =>
      extendedWarrantySkuIds.includes(s.skuId),
    );
    setLocalQuantity(pplusInBasket?.quantity ?? 0);

    if (
      warrantyService?.selected &&
      basketMode === BasketMode.Pre &&
      extendedWarrantySkuIds.length > 0
    ) {
      if (pplusServiceBase) {
        actions.removeService(pplusServiceBase.id);
      }
    }
  }, [variantServices, basketMode, extendedWarrantySkuIds, skuId]);

  const openPostAddToBasketDrawer = useCallback(
    (event: CustomEvent<{ errors: [] }>) => {
      const data = event.detail;
      const hasError = data.errors?.length > 0;

      const cachedBasketProduct = findCachedBasketProduct(skuId);

      const protectPlusService = cachedBasketProduct?.services.find((service) =>
        extendedWarrantySkuIds.includes(service.skuId),
      );

      if (
        !hasError &&
        cachedBasketProduct &&
        basketMode !== BasketMode.Pre &&
        cachedBasketProduct.quantity > (protectPlusService?.quantity ?? 0)
      ) {
        setIsDrawerOpen(true);
        dispatchMonetateQEvent(postAddToBasketDrawerEvent);
      }
    },
    [skuId],
  );

  const openDrawer = useCallback(() => {
    const { product, subTotal } = getBasket();
    if (product.length == 0 && subTotal != "0.00") {
      isBasketFull.current = true;
    } else {
      isBasketFull.current = false;
    }
    setIsDrawerOpen(true);
    dispatchMonetateQEvent(postAddToBasketDrawerEvent);
  }, [skuId]);

  const openPreAddToBasketDrawer = () => {
    setIsDrawerOpen(true);
  };

  // Deselect extended warranty services on successful basket add.
  useEffect(() => {
    const handleBasketSuccess = () => {
      availableServices
        .filter((s) => s.serviceType === "ExtendedWarranty" && !s.isMandatory)
        .forEach((s) => actions.removeService(s.id));
    };

    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      handleBasketSuccess,
    );
    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        handleBasketSuccess,
      );
    };
  }, [availableServices, actions]);

  useEffect(() => {
    if (basketMode == BasketMode.Pre) {
      window.addEventListener(
        PRE_ADD_TO_BASKET_DRAWER,
        openPreAddToBasketDrawer,
      );
    } else {
      window.addEventListener(OPEN_POST_ADD_TO_BASKET_DRAWER, openDrawer);
    }

    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      openPostAddToBasketDrawer,
    );

    return () => {
      if (basketMode == BasketMode.Pre) {
        window.removeEventListener(
          PRE_ADD_TO_BASKET_DRAWER,
          openPreAddToBasketDrawer,
        );
      } else {
        window.removeEventListener(OPEN_POST_ADD_TO_BASKET_DRAWER, openDrawer);
      }
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        openPostAddToBasketDrawer,
      );
    };
  }, [openPostAddToBasketDrawer, openDrawer, skuId]);

  const flushUpdatedCommerceItems = () => {
    if (commerceItems.current !== null) {
      dispatchEvent(
        new CustomEvent(ADD_TO_BASKET_ESI_CHILD_ITEMS_UPDATED_POST_A2B, {
          detail: {
            commerceItems: commerceItems.current,
          },
        }),
      );
      commerceItems.current = null;
    }
  };

  const closeDrawer = () => {
    flushUpdatedCommerceItems();
    setIsDrawerOpen(false);
  };

  // Sync local quantity and basket state when the drawer opens.
  useEffect(() => {
    const cachedBasketProduct = findCachedBasketProduct(skuId);

    if (isDrawerOpen && cachedBasketProduct) {
      const skuIdsToQuantityMap = cachedBasketProduct.services.reduce(
        (acc: Record<string, number>, curr) => {
          acc[curr.skuId] = curr.quantity;
          return acc;
        },
        {},
      );

      if (pplusServiceBase) {
        const qty = skuIdsToQuantityMap[pplusServiceBase.skuId] ?? 0;
        setLocalQuantity(qty);
      }
    }

    if (cachedBasketProduct && cachedBasketProduct.quantity > 0) {
      setComplianceDrawerHeader("Added to your basket");
      setBasketQuantity(cachedBasketProduct.quantity);
    } else {
      setComplianceDrawerHeader(drawerHeaderDefault);
      setBasketQuantity(0);
    }
  }, [isDrawerOpen]);

  function setServiceSelected(serviceId: string, selected = true) {
    if (selected) {
      actions.selectService(serviceId);
    } else {
      actions.removeService(serviceId);
    }
  }

  function findServiceInBasketCookie(
    service: TransformedExtendedWarrantyMulti,
  ) {
    const basketServices = findCachedBasketProduct(skuId)?.services || [];
    return basketServices.find((s: ServiceItem) => s.skuId === service.skuId);
  }

  const handleChangeQuantity = async (
    service: TransformedExtendedWarrantyMulti,
    quantity: number,
  ) => {
    const serviceToUpdate = findServiceInBasketCookie(service);

    if (!serviceToUpdate) {
      return false;
    }

    setApiError(false);

    const response = await changeItemQuantity(
      basketBaseUrl,
      serviceToUpdate.itemId,
      quantity,
    );

    if (response.success) {
      commerceItems.current = response.commerceItems;
      setLocalQuantity(quantity);
    } else {
      setApiError(true);
    }

    return response.success;
  };

  const handleToggleSelectedServiceClick = async (
    service: TransformedExtendedWarrantyMulti,
  ) => {
    setServiceSelected(service.id, true);
  };

  const handleAddProtectPlusClick = async (
    service: TransformedExtendedWarrantyMulti,
    quantity: number,
  ) => {
    const cachedBasketProduct = findCachedBasketProduct(skuId);
    if (!cachedBasketProduct) {
      return;
    }

    setApiError(false);

    const result = await addServiceToBasket(
      basketBaseUrl,
      cachedBasketProduct.itemId,
      service.id,
      quantity,
    );

    if (result.success) {
      commerceItems.current = result.commerceItems;
      setLocalQuantity(quantity);
      window.dispatchEvent(
        new CustomEvent("jl-events.browser.basketQuantityUpdated"),
      );
    } else {
      setApiError(true);
    }
  };

  if (!warrantyService) return null;

  return (
    <Drawer
      isOpen={isDrawerOpen}
      onClose={closeDrawer}
      heading={complianceDrawerHeader}
      className={styles.drawer}
      primaryButtonText="Continue shopping"
      onPrimaryButtonClick={closeDrawer}
      underlinedButtonText={""}
    >
      <div
        className={classNames(
          styles.drawer__titleItem,
          showTitleWithoutTick ? styles.drawer__titleItem_no_tick : "",
        )}
        data-testid={"post-a2b-drawer-product-title"}
      >
        {!showTitleWithoutTick && <Tick size="medium" />}
        <div>
          {basketQuantity > 0 && (
            <>
              <span> {basketQuantity} </span>
              <span className={styles.timesSymbol} aria-label="times">
                X
              </span>
            </>
          )}
          <span> {title} </span>
        </div>
      </div>
      {apiError && (
        <Message
          body="Sorry, something went wrong. Please try again later"
          type="error"
          className={styles.drawer__message}
        />
      )}
      {isBasketFull.current && (
        <Message
          body="You have too many items in your basket. We are unable to add Protect Plus currently"
          type="information"
          className={styles.drawer__message}
        />
      )}
      <ProtectPlusCompliance
        mode={basketMode}
        protectPlusScheme={protectPlusScheme}
        guarantees={guarantees}
        warrantyService={warrantyService}
        addProtectPlusClicked={async (service, serviceQuantity) => {
          if (service.quantity === serviceQuantity) {
            return true;
          }

          if (basketQuantity > 0) {
            await handleAddProtectPlusClick(service, serviceQuantity);
          } else {
            await handleToggleSelectedServiceClick(service);
          }

          return !apiError;
        }}
        closeDrawer={closeDrawer}
        maxAllowedServices={basketQuantity}
        handleStepperChange={handleChangeQuantity}
        hideFooter={hideFooter || isBasketFull.current}
        isDrawerOpen={isDrawerOpen}
      />
    </Drawer>
  );
};
